import { splitAccessorPath } from '../../../core/utils/accessors/split-accessor-path.js';
import type {
  SeriesPropsWithAxes,
  XYChartDataWithCorrespondingSeries,
} from '../types/xy-chart-internals.js';
import type { XYChartData, XYChartRectSeriesProps } from '../types/xy-chart.js';

export function getAccessor(s?: string) {
  if (!s) {
    return undefined;
  }
  return /\[|\./.test(s) ? splitAccessorPath(s) : [s];
}

/**
 * Combines root data with corresponding series that have axes information.
 *
 * @param rootData - The root data for the chart.
 * @param seriesWithAxes - The series with their corresponding axes properties.
 * @returns - An array of combined data with corresponding series.
 */
export function combineDataWithCorrespondingSeries(
  rootData: XYChartData[],
  seriesWithAxes: SeriesPropsWithAxes[],
): XYChartDataWithCorrespondingSeries[] {
  const combinedData: XYChartDataWithCorrespondingSeries[] = [
    {
      data: rootData,
      series: [],
    },
  ];
  const ROOT_DATA_POSITION = 0;

  for (const series of seriesWithAxes) {
    const { seriesProps } = series;

    const paths = {
      x0: getAccessor(seriesProps.x0Accessor),
      x1: getAccessor(seriesProps.x1Accessor),
      y0: getAccessor(seriesProps.y0Accessor),
      y1: getAccessor(seriesProps.y1Accessor),
      value: getAccessor((seriesProps as XYChartRectSeriesProps).valueAccessor),
    };

    if (seriesProps.data) {
      combinedData.push({
        data: seriesProps.data,
        series: [{ ...series, paths }],
      });
    } else {
      combinedData[ROOT_DATA_POSITION].series.push({ ...series, paths });
    }
  }

  return combinedData;
}
